import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  GetCustomerCredits,
  CreateCustomerCredit,
  GetCustomerCreditBalance,
  GetSupplierCredits,
  GetSupplierCreditBalance,
  CreateSupplierCredit,
  GetAllCustomerCredits,
  UpdateCustomerCreditNote,
  GetSupplierOutstandingPurchases,
  SupplierBulkPay,
  GetCustomerOutstandingOrders,
  GetCreditSummary,
  GetOutstandingOrders,
  GetOutstandingPurchases,
} from "../Api";
import { ServiceQueryOptions, unwrapApiResponse } from "./query_helpers";
import { DEFAULT_STALE_TIME, STALE_TIME_0 } from "../Constant/AppConstants";

export const creditQueryKeys = {
  customerCredits: (params?: Parameters<typeof GetCustomerCredits>[0]) =>
    ["customerCredits", params] as const,
  customerCreditBalance: (customerId: string) =>
    ["customerCreditBalance", customerId] as const,
  supplierCredits: (params?: Parameters<typeof GetSupplierCredits>[0]) =>
    ["supplierCredits", params] as const,
  supplierCreditBalance: (supplierId: string) =>
    ["supplierCreditBalance", supplierId] as const,
  supplierOutstandingPurchases: (supplierId: string) =>
    ["supplierOutstandingPurchases", supplierId] as const,
  creditSummary: () => ["creditSummary"] as const,
  outstandingOrders: () => ["outstandingOrders"] as const,
  outstandingPurchases: () => ["outstandingPurchases"] as const,
};

export const invalidateCreditQueries = (queryClient: any) => {
  queryClient.invalidateQueries({ queryKey: ["customerCredits"] });
  queryClient.invalidateQueries({ queryKey: ["customerCreditBalance"] });
  queryClient.invalidateQueries({ queryKey: ["supplierCredits"] });
  queryClient.invalidateQueries({ queryKey: ["supplierCreditBalance"] });
  queryClient.invalidateQueries({ queryKey: ["allCustomerCredits"] });
  queryClient.invalidateQueries({ queryKey: ["creditSummary"] });
  queryClient.invalidateQueries({ queryKey: ["outstandingOrders"] });
  queryClient.invalidateQueries({ queryKey: ["outstandingPurchases"] });
};

export const useCustomerCreditsQuery = (
  params: Parameters<typeof GetCustomerCredits>[0],
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: creditQueryKeys.customerCredits(params),
    queryFn: async () => unwrapApiResponse(await GetCustomerCredits(params)),
  });
};

export const useAllCustomerCreditsQuery = (
  params: Parameters<typeof GetAllCustomerCredits>[0],
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: ["allCustomerCredits", params],
    queryFn: async () => await GetAllCustomerCredits(params),
  });
};

export const useCustomerCreditsRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: Parameters<typeof GetCustomerCredits>[0]) =>
      queryClient.fetchQuery({
        staleTime: DEFAULT_STALE_TIME,
        queryKey: creditQueryKeys.customerCredits(params),
        queryFn: async () =>
          unwrapApiResponse(await GetCustomerCredits(params)),
      }),
  });
};

export const useCustomerCreditBalanceQuery = (
  customerId: string,
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: creditQueryKeys.customerCreditBalance(customerId),
    queryFn: async () =>
      unwrapApiResponse(await GetCustomerCreditBalance(customerId)),
  });
};

export const useSupplierCreditsQuery = (
  params: Parameters<typeof GetSupplierCredits>[0],
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: creditQueryKeys.supplierCredits(params),
    queryFn: async () => unwrapApiResponse(await GetSupplierCredits(params)),
  });
};

export const useSupplierCreditBalanceQuery = (
  supplierId: string,
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: creditQueryKeys.supplierCreditBalance(supplierId),
    queryFn: async () =>
      unwrapApiResponse(await GetSupplierCreditBalance(supplierId)),
  });
};

export const useCreateCustomerCreditMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof CreateCustomerCredit>[0]) =>
      CreateCustomerCredit(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateCreditQueries(queryClient);
        queryClient.invalidateQueries({ queryKey: ["orders"] });
        queryClient.invalidateQueries({ queryKey: ["orderById"] });
      }
    },
  });
};

export const useCreateSupplierCreditMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof CreateSupplierCredit>[0]) =>
      CreateSupplierCredit(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateCreditQueries(queryClient);
      }
    },
  });
};

export const useUpdateCustomerCreditNoteMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof UpdateCustomerCreditNote>[0]) =>
      UpdateCustomerCreditNote(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateCreditQueries(queryClient);
      }
    },
  });
};

export const useCreditService = (config?: {
  fetchCustomerCreditsParams?: Parameters<typeof GetCustomerCredits>[0];
  fetchCustomerCreditsEnabled?: boolean;
  fetchAllCustomerCreditsParams?: Parameters<typeof GetAllCustomerCredits>[0];
  fetchAllCustomerCreditsEnabled?: boolean;
  customerIdForBalance?: string;
  fetchCustomerBalanceEnabled?: boolean;
  fetchSupplierCreditsParams?: Parameters<typeof GetSupplierCredits>[0];
  fetchSupplierCreditsEnabled?: boolean;
  supplierIdForBalance?: string;
  fetchSupplierBalanceEnabled?: boolean;
}) => {
  const customerCreditsQuery = useCustomerCreditsQuery(
    config?.fetchCustomerCreditsParams!,
    {
      enabled:
        config?.fetchCustomerCreditsEnabled !== false &&
        config?.fetchCustomerCreditsParams !== undefined &&
        config?.fetchCustomerCreditsParams !== null,
    },
  );

  const allCustomerCreditsQuery = useAllCustomerCreditsQuery(
    config?.fetchAllCustomerCreditsParams!,
    {
      enabled:
        config?.fetchAllCustomerCreditsEnabled !== false &&
        config?.fetchAllCustomerCreditsParams !== undefined &&
        config?.fetchAllCustomerCreditsParams !== null,
    },
  );

  const customerCreditBalanceQuery = useCustomerCreditBalanceQuery(
    config?.customerIdForBalance!,
    {
      enabled:
        config?.fetchCustomerBalanceEnabled !== false &&
        !!config?.customerIdForBalance,
    },
  );

  const supplierCreditsQuery = useSupplierCreditsQuery(
    config?.fetchSupplierCreditsParams!,
    {
      enabled:
        config?.fetchSupplierCreditsEnabled !== false &&
        config?.fetchSupplierCreditsParams !== undefined &&
        config?.fetchSupplierCreditsParams !== null,
    },
  );

  const supplierCreditBalanceQuery = useSupplierCreditBalanceQuery(
    config?.supplierIdForBalance!,
    {
      enabled:
        config?.fetchSupplierBalanceEnabled !== false &&
        !!config?.supplierIdForBalance,
    },
  );

  const createCustomerCreditMutation = useCreateCustomerCreditMutation();
  const createSupplierCreditMutation = useCreateSupplierCreditMutation();
  const updateCustomerCreditNoteMutation =
    useUpdateCustomerCreditNoteMutation();

  return {
    customerCreditsQuery,
    allCustomerCreditsQuery,
    customerCreditBalanceQuery,
    supplierCreditsQuery,
    supplierCreditBalanceQuery,
    createCustomerCreditMutation,
    createSupplierCreditMutation,
    updateCustomerCreditNoteMutation,
  };
};

export const useSupplierOutstandingPurchasesQuery = (
  supplierId: string,
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: STALE_TIME_0,
    ...options,
    queryKey: creditQueryKeys.supplierOutstandingPurchases(supplierId),
    queryFn: async () =>
      unwrapApiResponse(
        await GetSupplierOutstandingPurchases({ supplier_id: supplierId }),
      ),
  });
};

export const useSupplierBulkPayMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: Parameters<typeof SupplierBulkPay>[0]) =>
      SupplierBulkPay(payload),
    onSuccess: (res) => {
      if (res?.success) {
        queryClient.invalidateQueries({ queryKey: ["supplierCredits"] });
        queryClient.invalidateQueries({ queryKey: ["supplierCreditBalance"] });
        queryClient.invalidateQueries({
          queryKey: ["supplierOutstandingPurchases"],
        });
        queryClient.invalidateQueries({ queryKey: ["purchases"] });
        queryClient.invalidateQueries({ queryKey: ["purchaseById"] });
      }
    },
  });
};

export const useCustomerOutstandingOrdersQuery = (
  customerId: string,
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: STALE_TIME_0,
    ...options,
    queryKey: ["customerOutstandingOrders", customerId],
    queryFn: async () =>
      unwrapApiResponse(await GetCustomerOutstandingOrders(customerId)),
  });
};

export const useCreditSummaryQuery = (options?: ServiceQueryOptions) => {
  return useQuery({
    staleTime: 0,
    refetchOnMount: "always" as const,
    ...options,
    queryKey: creditQueryKeys.creditSummary(),
    queryFn: async () => unwrapApiResponse(await GetCreditSummary()),
  });
};

export const useOutstandingOrdersQuery = (options?: ServiceQueryOptions) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: creditQueryKeys.outstandingOrders(),
    queryFn: async () => unwrapApiResponse(await GetOutstandingOrders()) ?? [],
  });
};

export const useOutstandingPurchasesQuery = (options?: ServiceQueryOptions) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: creditQueryKeys.outstandingPurchases(),
    queryFn: async () =>
      unwrapApiResponse(await GetOutstandingPurchases()) ?? [],
  });
};
